import React from 'react';
import {createRoot} from "react-dom/client";
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import "assets/scss/app.scss";
import App from './App';
import store from "store/store";
import {Provider} from "react-redux";
import sentryConfig from './configs/sentry';
import {
  AgentDocuments,
  Auth,
  AutoinvestRules, BorrowerRequirements,
  Control,
  Dashboard,
  Deposit,
  DetailedProject, DocumentsList,
  DocumentsToProject,
  Enter, FAQ,
  Identification,
  InvestmentConfirmation,
  Logout,
  MyDocumentsLayout,
  MyMoney,
  MyProjects,
  NotFound,
  Notifications,
  OverdueDebtRegulation,
  PersonalDataPolicy,
  Profile,
  Projects,
  RedirectAfterAuth,
  ReferralProgram,
  RegistrationRules,
  Rules,
  SignContract,
  Transactions,
  VerificationEmail,
  Withdraw
} from "./views";
import * as Sentry from "@sentry/react";
import {BrowserTracing} from "@sentry/tracing";
import {AboutUs, HowItWorks, News, Platform} from "./views/public";
import Public from "./views/public/Public/Public";
import {HowItWorksNavigation} from "./views/public/HowItWorksNavigation/HowItWorksNavigation";
import {VerifyEmail} from "./views/verification";
import {AuthProtectedRouteGroup} from "./components/routing";
import PrimaryMarket from "./views/dashboard/lender/investment/projects/PrimaryMarket";
import SecondaryMarket from "./views/dashboard/lender/investment/projects/SecondaryMarket";
import ProjectEconomicAnalysis from "./components/pages/detailed-project/ProjectEconomicAnalysis";
import ProjectLegalAnalysis from "./components/pages/detailed-project/ProjectLegalAnalysis";
import ProjectStrategy from "./components/pages/detailed-project/ProjectStrategy";
import ProjectDocuments from "./components/pages/detailed-project/ProjectDocuments";
import ProjectStages from "./components/pages/detailed-project";
import {MyInvestments, Orders, ActiveProjects} from "./components";
import {Survey} from "./components/pages/survey";
import ProjectPaymentSchedule from "./components/pages/detailed-project/ProjectPaymentSchedule";
import RiskDeclaration from "./views/public/RiskDeclaration";
import ElectronicDocuments from "./views/public/ElectronicDocuments";
import PersonalPolicyAccepted from "./views/public/PersonalPolicyAccepted";
import BorrowerInformation from "./views/public/BorrowerInformation";
import InvestorsInformation from "./views/public/InvestorsInformation";
import PublicDocumentsActual from "./views/public/PublicDocumentsActual/PublicDocumentsActual";
import PublicDocumentsArchive from "./views/public/PublicDocumentsArchive/PublicDocumentsArchive";
import PlatformDocuments from "./views/public/PlatformDocuments";
import MyOwnedProjects from "./views/dashboard/borrower/MyOwnedProjects";
import BorrowerDetailedProject from "./views/dashboard/borrower/BorrowerDetailedProject";
import {Role} from "./api-client";
import {useScrollToTop} from "./hooks";
import {BorrowerWizard} from "./views/dashboard/user/BorrowerWizard";
import {
  AutoInvest,
  CreateOffer,
  OrderConfirmation,
} from "./views/dashboard/lender/investment";
import AutoInvestConfirmation from "./views/dashboard/lender/investment/AutoInvest/AutoInvestConfirmation";
import OfferConfirmation from "./views/dashboard/lender/investment/offer/OfferConfirmation";
import PurchaseOffer from "./views/dashboard/lender/investment/purchase";
import PurchaseOfferConfirmation from "./views/dashboard/lender/investment/purchase/PurchaseConfirmation";
import MyArchive from "./views/dashboard/lender/stats/MyArchive";

const root = createRoot(
    document.getElementById('root') as HTMLElement
);

Sentry.init({
  dsn: sentryConfig.sentryDsn,
  integrations: [new BrowserTracing()],
  environment: process.env.NODE_ENV,
});

function Index() {
  useScrollToTop({
    exclude: [
      "/dashboard/project/:uuid/legal",
      "/dashboard/project/:uuid/strategy",
      "/dashboard/project/:uuid/economic",
      "/dashboard/project/:uuid/documents",
      "/dashboard/project/:uuid/stages",
      "/dashboard/project/:uuid/payment-schedule",
      "/dashboard/project/:uuid/chat",
    ],
    behavior: "smooth",
  });
  return (
    <Routes>
      <Route path="auth" element={<Auth/>}>
        <Route path="redirect-after-auth" element={<RedirectAfterAuth/>}/>
        <Route path="logout" element={<Logout/>}/>
        <Route path="verification/:verificationCode" element={<VerificationEmail/>}/>
      </Route>
      <Route path="enter/lender" element={<Enter role={Role.LENDER}/>}/>
      <Route path="enter/borrower" element={<Enter role={Role.BORROWER}/>}/>
      <Route path="dashboard" element={<Dashboard/>}>
        <Route element={<AuthProtectedRouteGroup/>}>
          <Route path="notifications" element={<Notifications/>}/>
          <Route path="survey/:uuid" element={<Survey/>}/>
          <Route path="identification" element={<Identification/>}/>
          <Route path="identification-borrower" element={<BorrowerWizard/>}/>
          <Route path="transactions" element={<Transactions/>}/>
          <Route path="transaction/:uuid"/>
          <Route path="my-money" element={<MyMoney/>}>
            <Route path="sign-contract" element={<SignContract/>}/>
          </Route>
          <Route path="my-projects" element={<MyProjects/>}>
            <Route index element={<MyInvestments/>}/>
            <Route path="my-investments" element={<MyInvestments/>}/>
            <Route path="active" element={<ActiveProjects/>}/>
            <Route path="orders" element={<Orders/>}/>
          </Route>
          <Route path="my-archive" element={<MyArchive/>}/>
          <Route path="withdraw" element={<Withdraw/>}/>
          <Route path="deposit" element={<Deposit/>}/>
          <Route path="my-documents" element={<MyDocumentsLayout/>}>
            <Route path="documents-to-project" element={<DocumentsToProject/>}/>
            <Route path="my-platform-documents" element={<AgentDocuments/>}/>
          </Route>
          <Route path="referral-program" element={<ReferralProgram/>}/>
          <Route path="market" element={<Projects/>}>
            <Route index element={<PrimaryMarket/>}/>
            <Route path="primary" element={<PrimaryMarket/>}/>
            <Route path="buyback" element={<SecondaryMarket/>}/>
          </Route>
          <Route path="autoinvest" element={<AutoInvest/>}/>
          <Route path="autoinvest/confirmation/:confirmationUuid" element={<AutoInvestConfirmation/>}/>
          <Route path="borrower-projects" element={<MyOwnedProjects/>}/>
          <Route path="borrower-project/:uuid" element={<BorrowerDetailedProject/>}/>
          <Route path="project/:uuid" element={<DetailedProject/>}>
            <Route index element={<ProjectEconomicAnalysis/>}/>
            <Route path="economic" element={<ProjectEconomicAnalysis/>}/>
            <Route path="legal" element={<ProjectLegalAnalysis/>}/>
            <Route path="strategy" element={<ProjectStrategy/>}/>
            <Route path="documents" element={<ProjectDocuments/>}/>
            <Route path="stages" element={<ProjectStages/>}/>
            <Route path="payment-schedule" element={<ProjectPaymentSchedule/>}/>
            <Route path="confirmation/:confirmationUuid" element={<InvestmentConfirmation/>}/>
            <Route path="order-confirmation/:confirmationUuid" element={<OrderConfirmation/>}/>
          </Route>
          <Route path="create-offer/:investmentUuid" element={<CreateOffer/>}>
            <Route path="confirmation/:confirmationUuid" element={<OfferConfirmation/>}/>
          </Route>
          <Route path="purchase-offer/:offerUuid" element={<PurchaseOffer/>}>
            <Route path="confirmation/:confirmationUuid" element={<PurchaseOfferConfirmation/>}/>
          </Route>
          <Route path="profile" element={<Profile/>}>
            <Route path="control" element={<Control/>}/>
          </Route>
          <Route path="*" element={<NotFound/>}/>
        </Route>
      </Route>
      <Route element={<Public/>}>
        <Route path="/" element={<App/>}/>
        <Route path="/rules_service" element={<Rules/>}/>
        <Route path="/rules_autoinvest" element={<AutoinvestRules/>}/>
        <Route path="/rules_registration" element={<RegistrationRules/>}/>
        <Route path="/personal_data_policy" element={<PersonalDataPolicy/>}/>
        <Route path="/risk_declaration" element={<RiskDeclaration/>}/>
        <Route path="/overdue_debt_regulation" element={<OverdueDebtRegulation/>}/>
        <Route path="/electronic_documents" element={<ElectronicDocuments/>}/>
        <Route path="/personal_policy_accepted" element={<PersonalPolicyAccepted/>}/>
        <Route path="/borrower-requirements" element={<BorrowerRequirements/>}/>
        <Route path="/documents-list" element={<DocumentsList/>}/>
        <Route path="/faq" element={<Navigate to="/how-it-works/faq" replace/>}/>
        <Route path="/how-it-works" element={<HowItWorksNavigation/>}>
          <Route index element={<HowItWorks/>}/>
          <Route path="faq" element={<FAQ/>}/>
        </Route>
        <Route path="/platform" element={<Platform/>}/>
        <Route path="/platform-documents" element={<PlatformDocuments/>}/>
        <Route path="/news" element={<News/>}/>
        <Route path="/about-us" element={<AboutUs/>}>
          <Route path="actual" element={<PublicDocumentsActual/>}/>
          <Route path="archive" element={<PublicDocumentsArchive/>}/>
        </Route>
        <Route path="/borrower-information" element={<BorrowerInformation/>}/>
        <Route path="/investors-information" element={<InvestorsInformation/>}/>
      </Route>
      <Route path="/verify-email/:verificationCode" element={<VerifyEmail/>}/>
      <Route path="*" element={<NotFound/>}/>
    </Routes>
  );
}

const Root = () => {
  //TODO: ADD FALLBACK COMPONENT
  return (
    <Sentry.ErrorBoundary onError={(error) => Sentry.captureException(error)}>
      <Provider store={store}>
        <BrowserRouter>
          <Index/>
        </BrowserRouter>
      </Provider>
    </Sentry.ErrorBoundary>
  );
};

root.render(<Root/>);
